import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { useDialogs } from "../../../../dialog/use-dialog";
import { useNotification } from "../../../../../context/notification-provider";
import { DistinctValue } from "../../../types";
import { markValuesAsValid } from "../../../services/cleaning-data-service";

interface ValidValuesFooterProps {
  fieldId: number | undefined;
  checked: { [key: string]: boolean };
  mainValues: DistinctValue[];
  refreshDistinctValues: () => void;
}

/**
 * Footer for Valid Values category (categoryId: 1)
 */
const ValidValuesFooter: React.FC<ValidValuesFooterProps> = ({
  fieldId,
  checked,
  mainValues,
  refreshDistinctValues,
}) => {
  const { confirm } = useDialogs();
  const { showNotification } = useNotification();

  const handleComplete = async () => {
    if (fieldId === undefined) {
      showNotification({
        message: "No field selected.",
        severity: "error",
        duration: 3000,
      });
      return;
    }

    // Get values to complete: prioritize checked items, then all values
    let valuesToComplete: string[] = [];
    const checkedValues = mainValues
      .filter((val) => checked[val.id])
      .map((val) => val.value);

    if (checkedValues.length > 0) {
      valuesToComplete = checkedValues;
    } else {
      // If nothing checked, complete all values in the list
      valuesToComplete = mainValues.map((val) => val.value);
    }

    if (valuesToComplete.length === 0) {
      showNotification({
        message: "No values available to complete.",
        severity: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      const result = await confirm(
        `Are you sure you want to mark ${valuesToComplete.length} value${valuesToComplete.length > 1 ? "s" : ""} as valid? This action will add them to the Valid Values list.`,
        {
          title: "Confirm Valid Values",
          okText: "Complete",
          cancelText: "Cancel",
          severity: "info",
        },
      );

      if (result === true) {
        try {
          await markValuesAsValid(fieldId, valuesToComplete);
          refreshDistinctValues();
          showNotification({
            message: "Values marked as valid successfully.",
            severity: "success",
            duration: 3000,
          });
        } catch (err) {
          showNotification({
            message: `Failed to mark values as valid. Reason: ${(err as Error).message}`,
            severity: "error",
            duration: 3000,
          });
        }
      }
    } catch {
      showNotification({
        message: "Failed to show confirmation dialog.",
        severity: "error",
        duration: 3000,
      });
    }
  };

  // Determine the message to display
  const checkedCount = Object.values(checked).filter(Boolean).length;
  const totalCount = mainValues.length;
  let message = `Complete all ${totalCount} value${totalCount === 1 ? "" : "s"} and add to Valid Values.`;
  if (checkedCount > 0) {
    message = `Complete ${checkedCount} checked value${checkedCount === 1 ? "" : "s"} and add to Valid Values.`;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          borderTop: "1px solid #eee",
          p: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
          {message}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          borderTop: "1px solid #eee",
          p: 2,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={{ mr: 1, borderRadius: 1 }}
          onClick={handleComplete}
        >
          Complete
        </Button>
      </Box>
    </Box>
  );
};

export default ValidValuesFooter;
